import { useCallback, Dispatch, SetStateAction } from "react";
import {
  fetchSalesDesignations,
  fetchUsersByDesignation,
  fetchGeographicalLevels,
  fetchGeographicalLocations,
  fetchGeographicalLocationsUnder,
  fetchChildrenUsers,
} from "../../../services/reportsDataService";
import { newReportConfig } from "../mdmReportsUtils";
import { DrillDownPathItem } from "../utils/hierarchyHelpers";

interface UseHierarchyLoadersParams {
  selectedReport: newReportConfig | null;
  filters: Record<string, string[]>;
  setOptionsMap: Dispatch<SetStateAction<Record<string, { label: string; value: string }[]>>>;
  setLoadingMap: Dispatch<SetStateAction<Record<string, boolean>>>;
  salesDrillDownPath: DrillDownPathItem[];
  geoDrillDownPath: DrillDownPathItem[];
  geoHierarchyOrder: string[];
  setGeoHierarchyOrder: Dispatch<SetStateAction<string[]>>;
  salesOptionsCache: Record<string, { label: string; value: string }[]>;
  setSalesOptionsCache: Dispatch<SetStateAction<Record<string, { label: string; value: string }[]>>>;
  geoOptionsCache: Record<string, { label: string; value: string }[]>;
  setGeoOptionsCache: Dispatch<SetStateAction<Record<string, { label: string; value: string }[]>>>;
}

export const useHierarchyLoaders = ({
  selectedReport,
  filters,
  setOptionsMap,
  setLoadingMap,
  salesDrillDownPath,
  geoDrillDownPath,
  geoHierarchyOrder,
  setGeoHierarchyOrder,
  salesOptionsCache,
  setSalesOptionsCache,
  geoOptionsCache,
  setGeoOptionsCache,
}: UseHierarchyLoadersParams) => {
  const loadSalesLevels = useCallback(async () => {
    if (!selectedReport?.salesHierarchyFilter?.enabled) return;

    const key = selectedReport.salesHierarchyFilter.levelFilterField;
    setLoadingMap(prev => ({ ...prev, [key]: true }));
    try {
      let availableLevels: string[] = [];

      const hierarchyOrder = selectedReport.salesHierarchyFilter.hierarchyOrder || [];

      if (hierarchyOrder.length > 0) {
        // Filter out 'supplier' from UI while keeping it in config
        availableLevels = hierarchyOrder.filter(level => level !== 'supplier');
      } else {
        const fallbackLevels = await fetchSalesDesignations("admin", "all");
        // Filter out 'supplier' from fallback levels as well
        availableLevels = fallbackLevels.filter(level => level !== 'supplier');
      }

      const opts = availableLevels.map(level => ({ label: level, value: level }));
      setOptionsMap(prev => ({ ...prev, [key]: opts }));
    } catch (error) {
      setOptionsMap(prev => ({ ...prev, [key]: [] }));
    } finally {
      setLoadingMap(prev => ({ ...prev, [key]: false }));
    }
  }, [selectedReport?.salesHierarchyFilter, setLoadingMap, setOptionsMap]);

  const loadSalesValues = useCallback(async (designation: string) => {
    if (!selectedReport?.salesHierarchyFilter?.enabled) return;

    console.log('[useHierarchyLoaders] loadSalesValues START:', {
      designation,
      salesDrillDownPath,
      hasCache: !!salesOptionsCache[designation],
      cacheLength: salesOptionsCache[designation]?.length || 0
    });

    // Check cache, but verify parent context matches before using it
    const cachedOptions = salesOptionsCache[designation];
    const hierarchyOrder = selectedReport.salesHierarchyFilter.hierarchyOrder || [];
    const targetLevelIndex = hierarchyOrder.indexOf(designation);

    let hasValidParentContext = false;
    if (hierarchyOrder.length > 0 && targetLevelIndex !== -1) {
      // Check if we're at the first level
      if (targetLevelIndex === 0) {
        hasValidParentContext = true;
      } else {
        // Check if user is navigating UP the hierarchy (selections exist in child levels)
        let isNavigatingUp = false;
        for (let i = targetLevelIndex + 1; i < hierarchyOrder.length; i++) {
          const childLevel = hierarchyOrder[i];
          if (filters[childLevel] && filters[childLevel].length > 0) {
            isNavigatingUp = true;
            break;
          }
        }
        
        if (isNavigatingUp) {
          hasValidParentContext = true;
        } else {
          // Check for parent context in drillDownPath (committed selections)
          if (salesDrillDownPath.length > 0) {
            for (let i = salesDrillDownPath.length - 1; i >= 0; i--) {
              const pathItem = salesDrillDownPath[i];
              const pathLevelIndex = hierarchyOrder.indexOf(pathItem.level);
              if (pathLevelIndex !== -1 && pathLevelIndex < targetLevelIndex) {
                hasValidParentContext = true;
                break;
              }
            }
          }
          
          // Also check for parent context in filters (building phase - uncommitted selections)
          if (!hasValidParentContext) {
            for (let i = 0; i < targetLevelIndex; i++) {
              const parentLevel = hierarchyOrder[i];
              if (filters[parentLevel] && filters[parentLevel].length > 0) {
                hasValidParentContext = true;
                break;
              }
            }
          }
        }
      }
    } else if (targetLevelIndex === -1) {
      hasValidParentContext = true;
    }
    
    console.log('[useHierarchyLoaders] loadSalesValues - Cache check:', {
      designation,
      hasCache: !!cachedOptions,
      cacheLength: cachedOptions?.length || 0,
      hasValidParentContext,
      targetLevelIndex,
      willUseCache: cachedOptions && cachedOptions.length > 0 && hasValidParentContext
    });
    
    if (cachedOptions && cachedOptions.length > 0 && hasValidParentContext) {
      console.log('[useHierarchyLoaders] loadSalesValues - USING CACHE (no API call)');
      setOptionsMap(prev => ({ ...prev, [designation]: cachedOptions }));
      return;
    }
    
    console.log('[useHierarchyLoaders] loadSalesValues - MAKING API CALL');

    const key = designation;
    setLoadingMap(prev => ({ ...prev, [key]: true }));
    try {
      let users: string[] = [];

      const levelKey = selectedReport.salesHierarchyFilter.levelFilterField;
      const currentLevel = filters[levelKey]?.[0];
      const currentLevelValues = currentLevel ? filters[currentLevel] || [] : [];
      let parentUsers: string[] = [];
      
      // Priority 1: Find closest parent in filters (building phase - includes uncommitted selections)
      if (hierarchyOrder.length > 0 && targetLevelIndex !== -1) {
        // Search backwards from target level to find closest parent with values
        for (let i = targetLevelIndex - 1; i >= 0; i--) {
          const parentLevel = hierarchyOrder[i];
          const parentValues = filters[parentLevel] || [];
          if (parentValues.length > 0) {
            parentUsers = parentValues;
            console.log('[useHierarchyLoaders] loadSalesValues - Using closest parent from filters:', {
              parentLevel,
              parentValues: parentUsers
            });
            break;
          }
        }
      }
      
      // Priority 2: If no parent in filters, check drillDownPath (committed selections)
      if (parentUsers.length === 0 && salesDrillDownPath.length > 0 && hierarchyOrder.length > 0) {
        for (let i = salesDrillDownPath.length - 1; i >= 0; i--) {
          const pathItem = salesDrillDownPath[i];
          const pathLevelIndex = hierarchyOrder.indexOf(pathItem.level);
          
          if (pathLevelIndex !== -1 && targetLevelIndex !== -1 && pathLevelIndex < targetLevelIndex) {
            const itemsAtThisLevel = salesDrillDownPath
              .filter(item => item.level === pathItem.level)
              .map(item => item.value);
            parentUsers = itemsAtThisLevel;
            console.log('[useHierarchyLoaders] loadSalesValues - Using parent from drillDownPath:', {
              parentLevel: pathItem.level,
              parentValues: parentUsers
            });
            break;
          }
        }
      }

      if (parentUsers.length > 0) {
        const allChildrenPromises = parentUsers.map(userId =>
          fetchChildrenUsers(userId, designation)
        );

        const allChildrenResults = await Promise.all(allChildrenPromises);

        const allChildren = allChildrenResults.flat();
        users = Array.from(new Set(allChildren));
      } else {
        users = await fetchUsersByDesignation(designation);
      }

      const opts = users.map(user => ({ label: user, value: user }));
      console.log('[useHierarchyLoaders] loadSalesValues - API response:', {
        designation,
        usersCount: users.length,
        optionsCount: opts.length
      });
      setOptionsMap(prev => ({ ...prev, [key]: opts }));
      setSalesOptionsCache(prev => ({ ...prev, [designation]: opts }));
    } catch (error) {
      console.error('[useHierarchyLoaders] loadSalesValues - ERROR:', error);
      setOptionsMap(prev => ({ ...prev, [key]: [] }));
    } finally {
      setLoadingMap(prev => ({ ...prev, [key]: false }));
    }
  }, [
    selectedReport?.salesHierarchyFilter,
    salesDrillDownPath,
    filters,
    setLoadingMap,
    setOptionsMap,
    salesOptionsCache,
    setSalesOptionsCache,
  ]);

  const loadGeographicalLevels = useCallback(async () => {
    if (!selectedReport?.geographicalHierarchyFilter?.enabled) return;

    const levelKey = selectedReport.geographicalHierarchyFilter.levelFilterField;
    setLoadingMap(prev => ({ ...prev, [levelKey]: true }));
    try {
      const levelsFromApi = await fetchGeographicalLevels();
      const allLevels: string[] = [...levelsFromApi].reverse();
      setGeoHierarchyOrder(allLevels);

      const opts = allLevels.map(level => ({ label: level, value: level }));
      setOptionsMap(prev => ({ ...prev, [levelKey]: opts }));
    } catch (error) {
      setOptionsMap(prev => ({ ...prev, [levelKey]: [] }));
    } finally {
      setLoadingMap(prev => ({ ...prev, [levelKey]: false }));
    }
  }, [selectedReport?.geographicalHierarchyFilter, setGeoHierarchyOrder, setLoadingMap, setOptionsMap]);

  const loadGeographicalValues = useCallback(async (level: string) => {
    if (!selectedReport?.geographicalHierarchyFilter?.enabled) return;

    console.log('[useHierarchyLoaders] loadGeographicalValues START:', {
      level,
      geoDrillDownPath,
      geoHierarchyOrder,
      hasCache: !!geoOptionsCache[level],
      cacheLength: geoOptionsCache[level]?.length || 0
    });

    const key = level;
    setLoadingMap(prev => ({ ...prev, [key]: true }));

    try {
      const cachedOptions = geoOptionsCache[level];
      
      let hierarchyOrder: string[] = geoHierarchyOrder || [];
      if (hierarchyOrder.length === 0) {
        try {
          const levelsFromApi = await fetchGeographicalLevels();
          hierarchyOrder = [...levelsFromApi].reverse();
          setGeoHierarchyOrder(hierarchyOrder);
        } catch (error) {
          // If we can't get hierarchy order, don't use cache
        }
      }
      
      const targetLevelIndex = hierarchyOrder.indexOf(level);
      
      let hasValidParentContext = false;
      if (hierarchyOrder.length > 0 && targetLevelIndex !== -1) {
        // Check if we're at the first level
        if (targetLevelIndex === 0) {
          hasValidParentContext = true;
        } else {
          // Check if user is navigating UP the hierarchy (selections exist in child levels)
          let isNavigatingUp = false;
          for (let i = targetLevelIndex + 1; i < hierarchyOrder.length; i++) {
            const childLevel = hierarchyOrder[i];
            if (filters[childLevel] && filters[childLevel].length > 0) {
              isNavigatingUp = true;
              break;
            }
          }
          
          if (isNavigatingUp) {
            hasValidParentContext = true;
          } else {
            // Check for parent context in drillDownPath (committed selections)
            if (geoDrillDownPath.length > 0) {
              for (let i = geoDrillDownPath.length - 1; i >= 0; i--) {
                const pathItem = geoDrillDownPath[i];
                const pathLevelIndex = hierarchyOrder.indexOf(pathItem.level);
                if (pathLevelIndex !== -1 && pathLevelIndex < targetLevelIndex) {
                  hasValidParentContext = true;
                  break;
                }
              }
            }
            
            // Also check for parent context in filters (building phase - uncommitted selections)
            if (!hasValidParentContext) {
              for (let i = 0; i < targetLevelIndex; i++) {
                const parentLevel = hierarchyOrder[i];
                if (filters[parentLevel] && filters[parentLevel].length > 0) {
                  hasValidParentContext = true;
                  break;
                }
              }
            }
          }
        }
      } else if (targetLevelIndex === -1) {
        hasValidParentContext = true;
      }
      
      console.log('[useHierarchyLoaders] loadGeographicalValues - Cache check:', {
        level,
        hasCache: !!cachedOptions,
        cacheLength: cachedOptions?.length || 0,
        hasValidParentContext,
        targetLevelIndex,
        willUseCache: cachedOptions && cachedOptions.length > 0 && hasValidParentContext
      });
      
      if (cachedOptions && cachedOptions.length > 0 && hasValidParentContext) {
        console.log('[useHierarchyLoaders] loadGeographicalValues - USING CACHE (no API call)');
        setOptionsMap(prev => ({ ...prev, [level]: cachedOptions }));
        setLoadingMap(prev => ({ ...prev, [key]: false }));
        return;
      }
      
      console.log('[useHierarchyLoaders] loadGeographicalValues - MAKING API CALL');
      let locations: string[] = [];

      if (hierarchyOrder.length === 0) {
        const levelsFromApi = await fetchGeographicalLevels();
        hierarchyOrder = [...levelsFromApi].reverse();
        setGeoHierarchyOrder(hierarchyOrder);
      }

      const levelKey = selectedReport.geographicalHierarchyFilter.levelFilterField;
      const currentLevel = filters[levelKey]?.[0];
      const currentLevelValues = currentLevel ? filters[currentLevel] || [] : [];

      let parentLocations: Array<{ level: string; value: string }> = [];
      
      // Priority 1: Find closest parent in filters (building phase - includes uncommitted selections)
      if (hierarchyOrder.length > 0 && targetLevelIndex !== -1) {
        // Search backwards from target level to find closest parent with values
        for (let i = targetLevelIndex - 1; i >= 0; i--) {
          const parentLevel = hierarchyOrder[i];
          const parentValues = filters[parentLevel] || [];
          if (parentValues.length > 0) {
            parentLocations = parentValues.map(value => ({ level: parentLevel, value }));
            console.log('[useHierarchyLoaders] loadGeographicalValues - Using closest parent from filters:', {
              parentLevel,
              parentValues
            });
            break;
          }
        }
      }
      
      // Priority 2: If no parent in filters, check drillDownPath (committed selections)
      if (parentLocations.length === 0 && geoDrillDownPath.length > 0 && hierarchyOrder.length > 0) {
        for (let i = geoDrillDownPath.length - 1; i >= 0; i--) {
          const pathItem = geoDrillDownPath[i];
          const pathLevelIndex = hierarchyOrder.indexOf(pathItem.level);
          
          if (pathLevelIndex !== -1 && targetLevelIndex !== -1 && pathLevelIndex < targetLevelIndex) {
            const itemsAtThisLevel = geoDrillDownPath
              .filter(item => item.level === pathItem.level)
              .map(item => ({ level: item.level, value: item.value }));
            parentLocations = itemsAtThisLevel;
            console.log('[useHierarchyLoaders] loadGeographicalValues - Using parent from drillDownPath:', {
              parentLevel: pathItem.level,
              parentValues: parentLocations.map(p => p.value)
            });
            break;
          }
        }
      }

      if (parentLocations.length > 0) {
        const parentLevel = parentLocations[0].level;
        const allChildrenPromises = parentLocations.map(loc => {
          return fetchGeographicalLocationsUnder(parentLevel, loc.value, level);
        });

        const allChildrenResults = await Promise.all(allChildrenPromises);

        const allChildren = allChildrenResults.flat();
        locations = Array.from(new Set(allChildren));
      } else {
        locations = await fetchGeographicalLocations(level);
      }

      const opts = locations.map(location => ({ label: location, value: location }));
      console.log('[useHierarchyLoaders] loadGeographicalValues - API response:', {
        level,
        locationsCount: locations.length,
        optionsCount: opts.length
      });
      setOptionsMap(prev => ({ ...prev, [key]: opts }));
      setGeoOptionsCache(prev => ({ ...prev, [level]: opts }));
    } catch (error) {
      console.error('[useHierarchyLoaders] loadGeographicalValues - ERROR:', error);
      setOptionsMap(prev => ({ ...prev, [key]: [] }));
    } finally {
      setLoadingMap(prev => ({ ...prev, [key]: false }));
    }
  }, [
    selectedReport?.geographicalHierarchyFilter,
    geoDrillDownPath,
    geoHierarchyOrder,
    filters,
    setLoadingMap,
    setOptionsMap,
    setGeoHierarchyOrder,
    geoOptionsCache,
    setGeoOptionsCache,
  ]);

  return {
    loadSalesLevels,
    loadSalesValues,
    loadGeographicalLevels,
    loadGeographicalValues,
  };
};


